import { useEffect, useRef, useState, useMemo } from 'react';
import Item from '../../SearchSidebar/SearchItemResult';
import {
  DistributionList,
  Group,
  Individual,
  Team,
  RoleGroup,
  Forum,
} from '../../SearchSidebar/SearchParityResults';
import KeywordSearchMessageList from './KeywordSearchMessageList';
import { type KeywordSearchGeneralResult } from 'js-sdk/src/apis/SearchAPI';
import BEM from 'common/bem';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';
import { useSearchEntitiesQuery } from 'redux-stores/KeywordSearch/keywordSearchEntityListApi';
import { useRTKQueryInfiniteResults } from 'common/hooks/useRTKQueryInfiniteResults';
import type { Entity, Organization, TeamEntity, User, RoleGroupEntity } from 'types';
import { Card } from 'common/components/WebComponents';
import { groupByEntity } from 'common/utils/searchParity';

type SearchMetadata = {
  entityOrder?: string[];
  firstHit: number;
  totalHits: number;
  continuation?: string;
};

const classes = BEM.with('KeywordSearchSideBar');

const { setIsFetching } = actions;

type MobxProps = {
  currentOrganization: Organization;
  currentOrganizationId: string;
  currentUserId: string;
  isUserDndAfEnabledForCurrentOrg: (user: User) => boolean;
  trackingInboxSearchGainsight: (searchLocation: string) => void;
  rosterForumsIds: string[];
  isDelegationRoleUnavailableEnabled: boolean;
};

const KeywordSearchEntityList = ({
  currentOrganization,
  currentOrganizationId,
  currentUserId,
  isUserDndAfEnabledForCurrentOrg,
  trackingInboxSearchGainsight,
  rosterForumsIds,
  isDelegationRoleUnavailableEnabled,
}: MobxProps) => {
  const dispatch = useAppDispatch();
  const keywordSearchRef = useRef<HTMLDivElement>(null);
  const prevKeywordSearchTerm = useRef<string>('');
  const [entitySortMap, setEntitySortMap] = useState<Map<string, number>>(new Map());

  const { selectedConversationId, keywordSearchTerm } = useAppSelector(
    (state) => state.keywordSearch
  );

  const queryArguments = {
    searchValue: keywordSearchTerm,
    orgId: currentOrganizationId,
  };

  const shouldSkipQuery = keywordSearchTerm.trim().length < 2;
  const { results, isFetching, response } = useRTKQueryInfiniteResults(
    useSearchEntitiesQuery,
    {
      args: queryArguments,
    },
    { ref: keywordSearchRef, skipQuery: shouldSkipQuery }
  );

  const effectiveResults = useMemo(() => {
    return shouldSkipQuery ? [] : results;
  }, [shouldSkipQuery, results]);

  const continuation = response?.metadata?.continuation;

  useEffect(() => {
    if (isFetching && continuation === undefined) {
      dispatch(setIsFetching(true));
    } else {
      dispatch(setIsFetching(false));
    }
  }, [isFetching, dispatch, continuation]);

  useEffect(() => {
    if (prevKeywordSearchTerm.current === keywordSearchTerm) {
      const hasResults = (results && results?.length > 0) || false;
      if (!isFetching && hasResults) {
        trackingInboxSearchGainsight('inbox search');
      }
    } else {
      prevKeywordSearchTerm.current = keywordSearchTerm;
    }
  }, [isFetching, keywordSearchTerm, results, trackingInboxSearchGainsight]);

  useEffect(() => {
    if (response?.metadata) {
      const metadata = response.metadata as unknown as SearchMetadata;
      if (metadata.entityOrder) {
        const newEntitySortMap = new Map(
          metadata.entityOrder.map((type: string, index: number) => [type, index])
        );
        setEntitySortMap(newEntitySortMap);
      }
    }
  }, [response?.metadata]);

  const entities = useMemo(() => {
    return effectiveResults
      ? effectiveResults.map((result) => ({
          ...result.entity,
          tag: result.entity.tag,
        }))
      : [];
  }, [effectiveResults]);

  const groupedResults = useMemo(() => {
    return entities.length > 0
      ? groupByEntity(currentOrganization, entitySortMap, entities as unknown as Entity[], true)
          .map((group) => {
            if (group.entity_type === 'forum') {
              const filteredValues = group.values.filter((entity) =>
                rosterForumsIds.includes(entity.id)
              );
              return {
                ...group,
                values: filteredValues,
              };
            }
            return group;
          })
          .filter((group) => group.values.length > 0)
      : [];
  }, [entities, currentOrganization, entitySortMap, rosterForumsIds]);

  const hasSelectedConversation = Boolean(selectedConversationId && selectedConversationId.trim());

  const allowedEntityTypes = new Set([
    'user',
    'distributionList',
    'group',
    'team',
    'role_group',
    'patient_context_conversations',
    'message',
    'forum',
  ]);

  const isAllowedEntityType = (type?: string): boolean => allowedEntityTypes.has(type || '');

  const getEntityHeader = (entityType: string) => {
    switch (entityType) {
      case 'user':
        return 'Individuals';
      case 'distributionList':
        return 'Broadcast';
      case 'group':
        return 'Groups';
      case 'team':
        return 'Teams';
      case 'role_group':
        return 'Roles';
      case 'patient_context_conversations':
        return 'Patient Context';
      case 'message':
        return 'Messages';
      case 'forum':
        return 'Forums';
      default:
        return '';
    }
  };

  const buildIndividualEntity = (
    value: Entity,
    index: number,
    lastItem: boolean
  ): JSX.Element | null => {
    if (!value) return null;

    const userDndAfStatusForCurrentOrg = isUserDndAfEnabledForCurrentOrg(value as unknown as User);

    return (
      <Item key={`individual-${index}`} entity={value} lastItem={lastItem}>
        <Individual
          individual={value}
          showPresenceIndicator
          showDndAfStatus={userDndAfStatusForCurrentOrg}
          currentUserId={currentUserId}
          query={keywordSearchTerm}
          currentOrganization={currentOrganization}
          presenceSize={'small'}
          dataTestId={`keyword-search-result-individual-${index}`}
        />
      </Item>
    );
  };

  const buildDistributionListEntity = (
    value: Entity,
    index: number,
    lastItem: boolean
  ): JSX.Element | null => {
    if (!value) return null;

    return (
      <Item key={`distribution_list-${index}`} entity={value} lastItem={lastItem}>
        <DistributionList
          distributionList={value}
          query={keywordSearchTerm}
          dataTestId={`keyword-search-result-distribution-list-${index}`}
        />
      </Item>
    );
  };

  const buildGroupEntity = (
    value: Entity,
    index: number,
    lastItem: boolean,
    originalEntityType?: string,
    dataTestId?: string
  ): JSX.Element | null => {
    if (!value) return null;

    return (
      <Item
        key={`group-${index}`}
        entity={value}
        lastItem={lastItem}
        originalEntityType={originalEntityType}
      >
        <Group
          group={value}
          query={keywordSearchTerm}
          dataTestId={
            dataTestId ? `${dataTestId}-${index}` : `keyword-search-result-group-${index}`
          }
        />
      </Item>
    );
  };

  const buildTeamEntity = (
    value: TeamEntity,
    index: number,
    lastItem: boolean
  ): JSX.Element | null => {
    if (!value) return null;
    return (
      <Item key={`team-${index}`} entity={value} lastItem={lastItem}>
        <Team
          team={value}
          query={keywordSearchTerm}
          dataTestId={`keyword-search-result-team-${index}`}
        />
      </Item>
    );
  };

  const buildRoleGroupEntity = (
    value: RoleGroupEntity,
    index: number,
    lastItem: boolean,
    originalEntityType?: string
  ): JSX.Element | null => {
    if (!value) return null;
    return (
      <Item
        key={`role-group-${index}`}
        entity={value}
        lastItem={lastItem}
        originalEntityType={originalEntityType}
      >
        <RoleGroup
          currentOrganization={currentOrganization}
          currentUserId={currentUserId}
          query={keywordSearchTerm}
          roleGroup={value}
          userDndAfStatusForCurrentOrg={isUserDndAfEnabledForCurrentOrg(value as unknown as User)}
          isKeywordSearch
          dataTestId={`keyword-search-result-role-group-${index}`}
          isDelegationRoleUnavailableEnabled={isDelegationRoleUnavailableEnabled}
          isExtendedAutoForwardOptionsEnabled={
            !!(currentOrganization as Organization & { dndAutoForwardEntities?: boolean })
              ?.dndAutoForwardEntities
          }
        />
      </Item>
    );
  };

  const buildMessage = (value: KeywordSearchGeneralResult, index: number) => {
    if (!value) return null;
    return <KeywordSearchMessageList key={`message-${index}`} value={value} extraPadding={true} />;
  };

  const buildForumEntity = (
    value: Entity,
    index: number,
    lastItem: boolean,
    originalEntityType?: string
  ): JSX.Element | null => {
    if (!value) return null;
    return (
      <Item
        key={`forum-${index}`}
        entity={value}
        lastItem={lastItem}
        originalEntityType={originalEntityType}
      >
        <Forum
          forum={value}
          query={keywordSearchTerm}
          dataTestId={`keyword-search-result-forum-${index}`}
        />
      </Item>
    );
  };

  return (
    <div
      className={classes('results-container', {
        block: !hasSelectedConversation,
        none: hasSelectedConversation,
      })}
      ref={keywordSearchRef}
    >
      {groupedResults.map(
        (entity) =>
          isAllowedEntityType(entity?.entity_type) && (
            <div className={classes('conversation-result')} key={entity?.entity_type}>
              <Card
                header={getEntityHeader(entity?.entity_type)}
                theme="provider"
                type="category"
                dataTestId={
                  entity?.entity_type === 'distributionList'
                    ? 'keyword-search-result-card-distribution-list'
                    : `keyword-search-result-card-${entity?.entity_type}`
                }
              >
                <div className={classes('entities-results')}>
                  {entity?.values?.map((value, index) => {
                    if (!value) return null;
                    const lastItem = index === entity.values.length - 1;
                    switch (entity?.entity_type) {
                      case 'user':
                        return buildIndividualEntity(value, index, lastItem);
                      case 'distributionList':
                        return buildDistributionListEntity(value, index, lastItem);
                      case 'group':
                        return buildGroupEntity(value, index, lastItem);
                      case 'team':
                        return buildTeamEntity(value as TeamEntity, index, lastItem);
                      case 'role_group':
                        value.$entityType = 'group';
                        return buildRoleGroupEntity(
                          value as RoleGroupEntity,
                          index,
                          lastItem,
                          entity?.entity_type
                        );
                      case 'patient_context_conversations':
                        value.$entityType = 'group';
                        return buildGroupEntity(
                          value,
                          index,
                          lastItem,
                          entity?.entity_type,
                          'keyword-search-result-patient-context'
                        );
                      case 'message':
                        return buildMessage(
                          {
                            entity: value,
                          } as unknown as KeywordSearchGeneralResult,
                          index
                        );
                      case 'forum':
                        value.$entityType = 'group';
                        return buildForumEntity(value, index, lastItem, entity?.entity_type);
                      default:
                        return null;
                    }
                  })}
                </div>
              </Card>
            </div>
          )
      )}
    </div>
  );
};

export default mobxInjectSelect<{}, MobxProps>({
  messengerStore: [
    'currentOrganization',
    'currentOrganizationId',
    'isDelegationRoleUnavailableEnabled',
  ],
  sessionStore: ['currentUserId'],
  userStore: ['isUserDndAfEnabledForCurrentOrg'],
  trackerStore: ['trackingInboxSearchGainsight'],
  conversationStore: ['rosterForumsIds'],
})(KeywordSearchEntityList);
